/**
 * <product-feature-accordion>
 *
 * Accordion with synced media on desktop and inline disclosure on mobile.
 *
 * Behavior:
 *   - Desktop (≥960px): exactly one item expanded; the right-column media
 *     stack mirrors it. Clicking the active item advances to the next.
 *   - Mobile (<960px): standard multi-expand accordion with inline media.
 *   - Keyboard: ArrowDown/Up move focus within triggers, Home/End jump.
 */

class ProductFeatureAccordion extends HTMLElement {
  connectedCallback() {
    this.triggers = Array.from(this.querySelectorAll('.pfa__label'));
    this.items = Array.from(this.querySelectorAll('.pfa__item'));
    this.panels = Array.from(this.querySelectorAll('.pfa__content'));
    this.mediaItems = Array.from(this.querySelectorAll('.pfa__media-item'));
    this.captionText = this.querySelector('[data-pfa-caption]');
    this.captionIndex = this.querySelector('[data-pfa-caption-index]');

    if (this.triggers.length === 0) return;

    this.activeIndex = 0;

    this.handleClick = this.handleClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleBreakpointChange = this.handleBreakpointChange.bind(this);

    this.desktopMediaQuery = window.matchMedia('(min-width: 960px)');
    this.desktopMediaQuery.addEventListener('change', this.handleBreakpointChange);

    this.bindTriggers();
  }

  disconnectedCallback() {
    this.desktopMediaQuery?.removeEventListener('change', this.handleBreakpointChange);
  }

  handleBreakpointChange(e) {
    if (e.matches) {
      // → Desktop: mobile may have left 0 or many items open, but the
      // right-column media stack assumes exactly one active slide.
      // Snap back to single-expand on activeIndex (most-recently-opened
      // on mobile, or 0 at boot). Pause any inline panel videos so
      // they don't keep an audio/decode thread alive on hidden DOM.
      this.querySelectorAll('.pfa__panel-media video').forEach((v) => v.pause());
      const idx = this.activeIndex >= 0 ? this.activeIndex : 0;
      this.setActive(idx);
    } else {
      // → Mobile: the right-column media stack hides. Pause its videos
      // so they don't keep playing offscreen. Inline panel videos are
      // driven by toggleItem; nothing to do for them here.
      this.querySelectorAll('.pfa__media-item video').forEach((v) => v.pause());
    }
  }

  bindTriggers() {
    this.triggers.forEach((btn) => {
      btn.addEventListener('click', this.handleClick);
      btn.addEventListener('keydown', this.handleKeyDown);
    });
  }

  handleClick(e) {
    const index = parseInt(e.currentTarget.dataset.index, 10);
    // Desktop: exactly one item is always expanded — the right-column
    //   media stack needs exactly one active slide. Clicking the
    //   already-active item advances to the next (wraps at the end).
    // Mobile (<960px): images render inline inside each open panel,
    //   so the section behaves as a standard multi-expand accordion.
    //   Each tap toggles its own item; any number can be open at once.
    const isMobile = window.matchMedia('(max-width: 959px)').matches;
    if (isMobile) {
      this.toggleItem(index);
    } else {
      const nextIdx = this.activeIndex === index
        ? (index + 1) % this.triggers.length
        : index;
      this.setActive(nextIdx);
    }
  }

  toggleItem(index) {
    const trigger = this.triggers[index];
    const item = this.items[index];
    const panel = this.panels[index];
    if (!trigger) return;

    const willOpen = trigger.getAttribute('aria-expanded') !== 'true';
    trigger.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    if (item) item.dataset.open = willOpen ? 'true' : 'false';
    if (panel) panel.setAttribute('aria-hidden', willOpen ? 'false' : 'true');

    // Drive the inline panel video. The snippet only emits the
    // `autoplay` attribute on item 0 (is_active=true at render time);
    // items 1..N otherwise sit on their poster forever. `muted` and
    // `playsinline` are set server-side, so .play() is allowed.
    const panelVideo = panel?.querySelector('video');
    if (panelVideo) {
      if (willOpen) {
        panelVideo.currentTime = 0;
        panelVideo.play().catch(() => {});
      } else {
        panelVideo.pause();
      }
    }

    // Track most-recently-opened so the right-column media stack has a
    // sensible slide to show if the user later resizes to desktop.
    if (willOpen) this.activeIndex = index;
  }

  handleKeyDown(e) {
    const currentIdx = this.triggers.indexOf(e.currentTarget);
    let nextIdx = null;
    switch (e.key) {
      case 'ArrowDown':
        nextIdx = (currentIdx + 1) % this.triggers.length;
        break;
      case 'ArrowUp':
        nextIdx = (currentIdx - 1 + this.triggers.length) % this.triggers.length;
        break;
      case 'Home':
        nextIdx = 0;
        break;
      case 'End':
        nextIdx = this.triggers.length - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    this.triggers[nextIdx].focus();
  }

  setActive(index) {
    this.activeIndex = index;

    this.triggers.forEach((btn, i) => {
      const isActive = i === index;
      btn.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    });
    this.items.forEach((item, i) => {
      item.dataset.open = i === index ? 'true' : 'false';
    });
    this.panels.forEach((panel, i) => {
      panel.setAttribute('aria-hidden', i === index ? 'false' : 'true');
    });
    this.mediaItems.forEach((media, i) => {
      const isActive = i === index;
      media.classList.toggle('is-active', isActive);
      const video = media.querySelector('video');
      if (video) {
        if (isActive) {
          video.currentTime = 0;
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      }
    });

    if (this.captionText) {
      const activeItem = this.items[index];
      const title = activeItem?.dataset.title || '';
      this.captionText.textContent = title;
    }
    if (this.captionIndex) {
      this.captionIndex.textContent = String(index + 1).padStart(2, '0');
    }
  }
}

if (!customElements.get('product-feature-accordion')) {
  customElements.define('product-feature-accordion', ProductFeatureAccordion);
}
